import { useState } from "react";
import { fmtHm } from "./TaskDetailPanel";
import { STATUS, STATUS_ORDER, PRIORITY, RISK } from "../constants";
import { PriorityBadge, Avatar, RiskBadge } from "./ui";
import Icon from "./Icon";
import { isOverdue } from "../utils";
import { useAuth } from "../store/auth";
import { parseUiPrefs } from "../hooks";

// (El popover "Bitácora · N días" que vivía dentro de la tarjeta se eliminó:
// los avances ahora se muestran como cuadro colgante bajo la tarjeta.)

function TaskCard({ task, index, onOpen, onDragStart, selectMode, isSelected, onToggleSelect, compact }) {
  const overdue = isOverdue(task);
  const risk = RISK[task.risk] || RISK.low;
  const inProject = task.task_type === "proyecto" || task.project_name;
  // Porcentaje de avance mostrado en el pie. Una tarea de checklist lo deriva
  // de sus ítems marcados; el resto usa `progress_pct`. Antes esto alimentaba
  // una <ProgressBar> y el número no se veía en ninguna parte.
  const items = task.items || [];
  const pctAvance = task.task_type === "checklist_apertura"
    ? (items.length ? Math.round((items.filter((i) => i.done).length / items.length) * 100) : 0)
    : (task.progress_pct || 0);
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, task)}
      onClick={() => onOpen(task)}
      // Sin acento lateral de color (regla dura del usuario, "tipo IA").
      // Sistema de diseño 2026-09-18: borde azul tenue 1.5px como todas las
      // tarjetas — el índigo de antes era el color del módulo Tareas, y el
      // color de módulo solo se permite en nav, tag de página y botón primario.
      className={`rounded-xl border-[1.5px] border-linea bg-white cursor-pointer transition-all duration-150 hover:-translate-y-0.5 hover:shadow-pop ${compact ? "space-y-1 p-2" : "space-y-2 p-3"} ${risk.ring} ${task.updated_today ? "ring-1 ring-brand-300/50" : ""} ${isSelected ? "ring-2 ring-amber-400 bg-amber-50/40" : ""}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          {/* (Se quitó el "#N" de posición en columna: confundía a los usuarios.) */}
          {selectMode && (
            <input type="checkbox" checked={isSelected} onChange={(e) => { e.stopPropagation(); onToggleSelect?.(task.id); }}
              className="mr-1 h-4 w-4 accent-amber-500" onClick={(e) => e.stopPropagation()} />
          )}
          {task.has_progress_today && (
            <span className="inline-flex items-center gap-0.5 rounded-tag bg-emerald-50 px-1.5 py-0.5 text-[11px] font-bold text-emerald-600" title="Avance registrado hoy">
              <Icon name="chart" size={10} /> Avance
            </span>
          )}
          {task.updated_today && !task.has_progress_today && (
            <span className="inline-flex items-center rounded-tag bg-blue-50 px-1.5 py-0.5 text-[11px] font-bold text-blue-600" title="Modificada hoy">
              Hoy
            </span>
          )}
        </div>
        <span className="flex items-center gap-1">
          {task.risk && task.risk !== "low" && <RiskBadge risk={task.risk} compact />}
          <PriorityBadge priority={task.priority} />
        </span>
      </div>
      {/* Proyecto de origen: 11px en gris terciario, sin chip de color — el
          violeta era el acento del módulo Proyectos y aquí no corresponde. */}
      {task.project_name && (
        <span className="flex max-w-full items-center gap-1 text-[11px] text-tinta-3">
          <Icon name="folder" size={11} /><span className="truncate">{task.project_name}</span>
        </span>
      )}
      <p className="text-sm font-medium uppercase leading-snug line-clamp-3">{task.title}</p>
      {task.task_type === "checklist_apertura" && (task.items || []).length > 0 && (() => {
        const total = task.items.length, done = task.items.filter((i) => i.done).length;
        return (
          <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${done === total ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"}`} title="Checklist">
            <Icon name="checkSquare" size={11} /> {done}/{total}
          </span>
        );
      })()}
      {/* Pie en UNA sola línea: vencimiento a la izquierda, avance y
          responsables a la derecha. Se retiró la <ProgressBar> suelta: el
          porcentaje comunica lo mismo ocupando una fracción del alto, y el
          documento pide explícitamente no duplicarlo. El número NO existía
          antes en la tarjeta, así que sustituirla por el % no pierde dato. */}
      <div className="flex items-center justify-between gap-2">
        {task.due_date ? (
          <span className={`inline-flex items-center gap-1 text-[11px] ${overdue ? "font-semibold text-rose-600" : "text-tinta-3"}`}>
            {overdue && <Icon name="alert" size={12} />}{task.due_date}
          </span>
        ) : <span />}
        <span className="flex shrink-0 items-center gap-1.5">
          {pctAvance > 0 && (
            <span className="text-[12px] font-bold tabular-nums text-tinta-2">{pctAvance}%</span>
          )}
          <span className="flex -space-x-1">
            {task.assignees?.slice(0, 3).map((a) => <Avatar key={a.id} name={a.full_name} size={22} />)}
          </span>
        </span>
      </div>
      {task.pmo?.length > 0 && (
        <div className="flex items-center gap-1 text-[11px] text-tinta-2">
          <Icon name="shield" size={11} />
          <span className="truncate">{task.pmo.map((p) => p.full_name.split(" ")[0]).join(", ")}</span>
        </div>
      )}
    </div>
  );
}

// Cuadro colgante con los avances de una tarea, indentado bajo su tarjeta.
// El conector en "L" (codo) nace del borde inferior de la tarjeta PADRE y
// entra por la izquierda del cuadro — deja claro que los avances pertenecen
// a la tarjeta de arriba y no a la siguiente. Clic abre la misma tarea.
function ProgressSubCard({ task, onOpen }) {
  const briefs = task.progress_brief || [];
  // Últimos 3 avances, ordenados DESCENDENTE por fecha (el más reciente arriba).
  const shown = briefs.slice(-3).reverse();
  const openAtBitacora = () => {
    // Abrir el detalle directo en la pestaña "Registro de Avances".
    sessionStorage.setItem("tf:open-tab", "bitacora");
    onOpen(task);
  };
  return (
    <div className="flex pl-4">
      {/* Codo conector: nace justo en el borde inferior de la tarjeta padre
          (sin solaparla) y dobla hacia el cuadro de avances. */}
      <div className="h-5 w-3.5 shrink-0 rounded-bl-lg border-b-2 border-l-2 border-indigo-300" aria-hidden="true" />
      <button type="button" onClick={openAtBitacora} title="Ver el Registro de Avances"
        className="mt-1.5 min-w-0 flex-1 rounded-xl border border-indigo-200 bg-indigo-50/70 px-2 py-1.5 text-left transition-colors hover:bg-indigo-50">
        <div className="mb-1 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-indigo-500">
          <Icon name="trending-up" size={10} /> Avances
          {briefs.length > shown.length && (
            <span className="ml-auto rounded-tag bg-indigo-100 px-1.5 text-[11px] font-bold text-indigo-600" title={`Mostrando los últimos ${shown.length} de ${briefs.length}`}>
              {shown.length}/{briefs.length}
            </span>
          )}
        </div>
        <div className="space-y-1">
          {shown.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-x-1.5 text-[11px] leading-tight">
              <span className="font-medium text-slate-700">{p.author_name || "—"}</span>
              <span className="text-slate-400">{p.work_date}</span>
              <span className={`rounded px-1 text-[11px] font-semibold ${p.minutes > 0 ? "bg-indigo-100 text-indigo-700" : "bg-slate-200/70 text-slate-400"}`}>
                {p.minutes > 0 ? `${p.minutes} min` : "sin tiempo"}
              </span>
            </div>
          ))}
        </div>
      </button>
    </div>
  );
}

const PAGE = 40; // cards rendered per status column before "Cargar más" (keeps the board light)

export default function KanbanBoard({ tasks, onOpen, onStatusChange, onStageChange, workflow, selectMode, selected, onToggleSelect, presorted = false }) {
  const { user } = useAuth();
  const uiPrefs = parseUiPrefs(user);
  const compact = uiPrefs.density === "compact";
  // Flujo configurable por equipo (Fase 3.2): si el equipo tiene etapas, las
  // columnas son esas (cada una mapea a un estado base); si no, el estándar.
  const usingWorkflow = Array.isArray(workflow) && workflow.length > 0 && typeof onStageChange === "function";
  const baseFirstCol = {};
  if (usingWorkflow) workflow.forEach((c) => { if (!(c.base in baseFirstCol)) baseFirstCol[c.base] = c.key; });
  const colKeyOf = (t) => {
    if (usingWorkflow) {
      if (t.stage && workflow.some((c) => c.key === t.stage)) return t.stage;
      return baseFirstCol[t.status] ?? workflow[0].key;
    }
    return t.status;
  };
  // Lista normalizada de columnas: {key, label, dotClass?, color?, base?}
  const cols = usingWorkflow
    ? workflow.map((c) => ({ key: c.key, label: c.label, color: c.color, base: c.base }))
    : STATUS_ORDER.filter((s) => !(uiPrefs.hidden_kanban_statuses || []).includes(s))
        .map((s) => ({ key: s, label: STATUS[s].label, dotClass: STATUS[s].dot }));
  const [dragId, setDragId] = useState(null);
  const [limits, setLimits] = useState({}); // status -> how many cards to show
  const showMore = (s) => setLimits((m) => ({ ...m, [s]: (m[s] || PAGE) + PAGE }));
  const onDragStart = (e, task) => {
    setDragId(task.id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDrop = (col) => {
    if (dragId != null) {
      if (usingWorkflow) onStageChange(dragId, col.key, col.base);
      else onStatusChange(dragId, col.key);
    }
    setDragId(null);
  };

  const byStatus = (s) => {
    const col = tasks.filter((t) => colKeyOf(t) === s);
    // Si la página ya ordenó según la vista preferida del usuario, respetar
    // ese orden dentro de cada columna en vez del heurístico por defecto.
    if (presorted) return col;
    col.sort((a, b) => {
      // tasks with progress today first
      if (a.has_progress_today !== b.has_progress_today) return a.has_progress_today ? -1 : 1;
      // overdue first
      const today = new Date().toISOString().slice(0, 10);
      const aOver = a.due_date && a.due_date < today ? 1 : 0;
      const bOver = b.due_date && b.due_date < today ? 1 : 0;
      if (aOver !== bOver) return bOver - aOver;
      // due soonest next
      if (a.due_date && b.due_date) return a.due_date.localeCompare(b.due_date);
      if (a.due_date) return -1;
      if (b.due_date) return 1;
      // newest created last
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
    return col;
  };

  const total = tasks.length;

  return (
    <div className={`flex overflow-x-auto pb-4 ${compact ? "gap-3" : "gap-4"}`}>
      {cols.map((cdef) => {
        const s = cdef.key;
        const col = byStatus(s);
        const overdueCount = col.filter((t) => isOverdue(t)).length;
        const colHours = col.reduce((a, t) => a + (t.hours_spent || 0), 0);
        const pct = total ? Math.round((col.length / total) * 100) : 0;
        return (
          <div
            key={s}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(cdef)}
            className={`flex min-w-[250px] flex-1 shrink-0 flex-col rounded-xl border border-slate-200/70 bg-slate-100/70 shadow-[inset_0_1px_4px_rgba(16,24,40,.05)] ${compact ? "p-1.5" : "p-2.5"}`}
          >
            <div className="mb-2.5 flex items-center justify-between px-1">
              <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                {cdef.dotClass
                  ? <span className={`h-2.5 w-2.5 rounded-full ${cdef.dotClass}`} />
                  : <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: cdef.color || "#64748b" }} />}
                {cdef.label}
              </div>
              <span className="chip bg-white font-bold text-slate-500 shadow-soft">{col.length}</span>
            </div>
            <div className={`flex flex-1 flex-col ${compact ? "gap-1.5" : "gap-2"}`}>
              {col.slice(0, limits[s] || PAGE).map((t, i) => (
                <div key={t.id}>
                  <TaskCard task={t} index={i + 1} onOpen={onOpen} onDragStart={onDragStart}
                    selectMode={selectMode} isSelected={selected?.has(t.id)} onToggleSelect={onToggleSelect} compact={compact} />
                  {/* Bitácora como cuadro COLGANTE indentado bajo la tarjeta
                      (no dentro): autor, fecha del avance y minutos. */}
                  {t.progress_brief?.length > 0 && <ProgressSubCard task={t} onOpen={onOpen} />}
                </div>
              ))}
              {col.length === 0 && <div className="py-6 text-center text-xs text-slate-400">—</div>}
              {col.length > (limits[s] || PAGE) && (
                <button onClick={() => showMore(s)}
                  className="mt-1 rounded-xl border border-dashed border-brand-300 bg-white/70 py-2 text-xs font-semibold text-brand-700 transition hover:bg-brand-50">
                  Cargar más ({col.length - (limits[s] || PAGE)} restantes)
                </button>
              )}
            </div>
            <div className="mt-2 border-t border-slate-200/70 px-1 pt-2 text-[11px]">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-500">Total: <span className="tabular-nums text-slate-700">{col.length}</span></span>
                {overdueCount > 0 && <span className="font-semibold text-rose-600">{overdueCount} venc.</span>}
              </div>
              <div className="mt-0.5 flex items-center justify-between text-slate-400">
                <span className="tabular-nums">{pct}% del total</span>
                {colHours > 0 && <span className="tabular-nums">{fmtHm(colHours)}</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
